import React, { useState } from "react";
import { Box, Button, Chip } from "@mui/material";
import { setupManagementUrl } from "../../../../config";
import CommissionForm from "./forms";
import DynamicTableForJson from "../../../../data/DynamicTable/dynamicTableForJson";
import Joyride from "react-joyride";
import Swal from "sweetalert2";
import { executeAnyMutation } from "../../../../data/DynamicServiceNew/jsondynamicservice";

const base_url = setupManagementUrl.uri;

// Status is an int (nullable) on the Commission entity:
//   null -> Pending, 0 -> Declined, 1 -> Approved, 2 -> Initiated
const COMMISSION_STATUS = {
  PENDING: null,
  DECLINED: 0,
  APPROVED: 1,
  INITIATED: 2,
};

const COMMISSION_STATUS_LABELS = {
  null: "Pending",
  0: "Declined",
  1: "Approved",
  2: "Initiated",
};

const COMMISSION_STATUS_COLORS = {
  null: "default",
  0: "error",
  1: "success",
  2: "warning",
};

// Normalizes a possibly-undefined status value to the int-keyed lookups
// above. Status comes back from the API as a real int (or null), so this
// mainly guards against undefined.
const getStatusKey = (status) => (status === undefined ? null : status);

const getStatusLabel = (status) =>
  COMMISSION_STATUS_LABELS[getStatusKey(status)] ?? COMMISSION_STATUS_LABELS[null];

const getStatusColor = (status) =>
  COMMISSION_STATUS_COLORS[getStatusKey(status)] ?? COMMISSION_STATUS_COLORS[null];

// Columns reflect the actual Commission entity fields — see the
// createCommission response shape: Id, ProductId, CustomerId, LoanId,
// CommissionPercentage, CommissionAmount, IsActive, Status, CompanyId,
// RegionId, BranchId. There is no Name/Description/Amount/Percentage/
// CommissionType/EffectiveFrom/EffectiveTo on this entity.
const columns = [
  {
    field: "LoanId",
    headerName: "Loan ID",
    width: 120,
  },
  {
    field: "CustomerName",
    headerName: "Customer",
    width: 130,
  },
  {
    field: "ProductId",
    headerName: "Product ID",
    width: 120,
  },
  {
    field: "ReferralAgent" ,
    headerName: "Referral Agent",
    width: 120,
  },
  {
  field: "AmountToDisburse",
  headerName: "Principal",
  width: 120,
  valueFormatter: ({ value }) =>
  value == null
    ? ""
    : Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
  },
   {
    field: "InitiatedByName" ,
    headerName: "Initiated By",
    width: 120,
  },

  {
    field: "CommissionPercentage",
    headerName: "Commission %",
    width: 140,
    valueGetter: (params) =>
      params.row.CommissionPercentage != null
        ? `${params.row.CommissionPercentage}%`
        : "-",
  },
  {
    field: "CommissionAmount",
    headerName: "Commission Amount",
    width: 170,
    valueGetter: (params) =>
      params.row.CommissionAmount != null
        ? params.row.CommissionAmount.toLocaleString()
        : "-",
  },
  {
    field: "Status",
    headerName: "Status",
    width: 150,
    renderCell: (params) => (
      <Chip
        label={getStatusLabel(params.row.Status)}
        size="small"
        color={getStatusColor(params.row.Status)}
        sx={{ fontWeight: 600 }}
      />
    ),
  },
];

const commissionTourSteps = [
  {
    target: ".dynamic-table-header",
    content: "This is the Commission Management page where you can initiate commissions for approval.",
    placement: "top",
    disableBeacon: true,
  },
  {
    target: ".dynamic-table-search",
    content: "Use the search bar to find specific commissions.",
    placement: "bottom",
  },
  {
    target: ".dynamic-table-add-button",
    content: "Click this button to add a new commission.",
    placement: "right",
  },
  {
    target: ".dynamic-table-actions",
    content: "Select actions like Edit or Initiate for each commission from this dropdown.",
    placement: "left",
  },
];

const CommissionTable = () => {
  const [runTour, setRunTour] = useState(false);

  const getUserId = () => {
    const user = JSON.parse(localStorage.getItem("decodedToken") || "{}");
    return user.Id || null;
  };

  const handleInitiateCommission = async (row, refetch) => {
    // A commission can only move from Pending -> Initiated. Guard against
    // re-initiating something that's already been declined, approved, or
    // initiated, since the "Initiate" action button doesn't otherwise know
    // the current status.
    if (row.Status !== null && row.Status !== undefined) {
      await Swal.fire(
        "Cannot Initiate",
        `This commission is already "${getStatusLabel(row.Status)}" and cannot be initiated again.`,
        "warning"
      );
      return;
    }

    const confirm = await Swal.fire({
      title: "Initiate Commission?",
      text: `Are you sure you want to initiate this commission for Loan #${row.LoanId}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Initiate",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    // Optional: Prompt for initiation reason
    const { value: initiateReason } = await Swal.fire({
      title: "Initiation Reason (Optional)",
      input: "textarea",
      inputPlaceholder: "Enter reason for initiating this commission...",
      showCancelButton: true,
      confirmButtonText: "Submit",
      cancelButtonText: "Skip",
    });

    const userId = getUserId();
    const currentDate = new Date().toISOString();

    // FIX: the payload previously used fields (name, description, amount,
    // percentage, commissionType, effectiveFrom/To) that don't exist on the
    // Commission entity at all — see the actual createCommission response
    // shape: ProductId, CustomerId, LoanId, CommissionPercentage,
    // CommissionAmount, CompanyId, RegionId, BranchId, IsActive, Status.
    // Sending the wrong field names would silently drop/null the real
    // values on the backend instead of preserving them.
    const response = await executeAnyMutation(
      "updateCommission",
      {
        id: row.Id,
        // Preserve existing fields using their real names
        productId: row.ProductId,
        customerId: row.CustomerId,
        loanId: row.LoanId,
        commissionPercentage: row.CommissionPercentage,
        commissionAmount: row.CommissionAmount,
        companyId: row.CompanyId,
        regionId: row.RegionId,
        branchId: row.BranchId,
        isActive: row.IsActive,
        // Initiation fields
        initiatedBy: parseInt(userId),
        initiatedDate: currentDate,
        // initiateReason: initiateReason || null,
        status: 2, // 2 = Initiated
      },
      "result",
      false,
      base_url
    );

    if (response?.results) {
      await Swal.fire(
        "Initiated!",
        "Commission has been initiated for approval.",
        "success"
      );
      if (refetch) refetch();
    } else {
      await Swal.fire(
        "Error!",
        "Could not initiate the commission. Please try again.",
        "error"
      );
    }
  };

  const actions = {
    entity: localStorage.getItem("SelectedRight"),
    edit: {
      key: "EDIT",
      button_name: "Edit",
      Show_Button: true,
      permission: "EditPermission",
    },
    add: {
      key: "ADD",
      button_name: "Add",
      Show_Button: true,
      permission: "CreatePermission",
    },

    initiate: {
      key: "INITIATE",
      button_name: "Initiate",
      Show_Button: true,
      permission: "EditPermission",
      onClick: (row, refetch) => handleInitiateCommission(row, refetch),
    },
    dataObject: {
      ignoreFields: ["UpdateDate", "CreateDate", "ApprovedBy", "ApprovedDate", "InitiatedBy", "InitiatedDate"],
      objectName: "allCommissionsAwaitingApproval",
      parameters: "",
    },
    showStatus: true,
    showMore: {
      search: false,
      min: 0,
      max: 0,
    },
  };

  return (
    <Box m="20px">
      <Joyride
        steps={commissionTourSteps}
        run={runTour}
        continuous
        showSkipButton
        showProgress
        styles={{
          options: {
            primaryColor: "#1976d2",
            zIndex: 10000,
          },
        }}
      />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Button onClick={() => setRunTour(true)} variant="outlined" color="primary">
          Start Page Tour
        </Button>
      </Box>

      <DynamicTableForJson
        title="Commissions Management"
        subtitle="Manage & Initiate Commissions"
        FormComponent={CommissionForm}
        columns={columns}
        base_url={base_url}
        actions={actions}
        tourSteps={commissionTourSteps}
      />
    </Box>
  );
};

export default CommissionTable;